
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { I18nextProvider } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { SpaceProvider } from './contexts/SpaceContext';
import { SecurityAssistantProvider } from './contexts/SecurityAssistantContext';
import { AppLayout } from './components/Layout/AppLayout';
import { ProtectedRoute, AdminRoute } from './components/Auth';
import { theme } from './theme/theme';
import i18n from './i18n/config';
import { Dashboard } from './pages/Dashboard';
import { TeamManagement } from './pages/TeamManagement';
import { TeamDetail } from './pages/TeamDetail';
import { SpaceManagement } from './pages/SpaceManagement';
import { SpaceDetail } from './pages/SpaceDetail';
import { SpaceSettings } from './pages/SpaceSettings';
import { DiagramPage } from './pages/DiagramPage';
import { DiagramsPage } from './pages/DiagramsPage';
import { ComponentList } from './pages/ComponentList';
import { ComponentDetail } from './pages/ComponentDetail';
import { RiskManagement } from './pages/RiskManagement';
import { RiskDetail } from './pages/RiskDetail';
import { MitigationDetail } from './pages/MitigationDetail';
import { EmbedView } from './pages/EmbedView';
import { ReportsPage } from './pages/ReportsPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { UserProfile } from './pages/UserProfile';
import { UserSettings } from './pages/UserSettings';
import { AdminDashboard } from './pages/AdminDashboard';
import { UserManagement } from './pages/UserManagement';
import { SystemSettings } from './pages/SystemSettings';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                {/* Public routes - no auth required */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/embed/:token" element={<EmbedView />} />
                {/* Auth callback route for OIDC redirect */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* Protected routes - require authentication */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <SpaceProvider>
                        <SecurityAssistantProvider>
                          <AppLayout>
                            <Routes>
                              <Route path="/" element={<Dashboard />} />
                              <Route
                                path="/diagrams"
                                element={<DiagramsPage />}
                              />
                              <Route
                                path="/spaces"
                                element={<SpaceManagement />}
                              />
                              <Route
                                path="/spaces/:spaceId"
                                element={<SpaceDetail />}
                              />
                              <Route
                                path="/spaces/:spaceId/settings"
                                element={<SpaceSettings />}
                              />
                              <Route
                                path="/spaces/:spaceId/diagrams/:diagramId"
                                element={<DiagramPage />}
                              />
                              <Route
                                path="/components"
                                element={<ComponentList />}
                              />
                              <Route
                                path="/components/:componentId"
                                element={<ComponentDetail />}
                              />
                              <Route
                                path="/risks"
                                element={<RiskManagement />}
                              />
                              <Route
                                path="/risks/:riskId"
                                element={<RiskDetail />}
                              />
                              <Route
                                path="/mitigations/:mitigationId"
                                element={<MitigationDetail />}
                              />
                              <Route
                                path="/reports"
                                element={<ReportsPage />}
                              />

                              {/* User routes */}
                              <Route
                                path="/profile"
                                element={<UserProfile />}
                              />
                              <Route
                                path="/settings"
                                element={<UserSettings />}
                              />

                              {/* Admin routes */}
                              <Route
                                path="/admin"
                                element={
                                  <AdminRoute>
                                    <AdminDashboard />
                                  </AdminRoute>
                                }
                              />
                              <Route
                                path="/admin/users"
                                element={
                                  <AdminRoute>
                                    <UserManagement />
                                  </AdminRoute>
                                }
                              />
                              <Route
                                path="/admin/teams"
                                element={
                                  <AdminRoute>
                                    <TeamManagement />
                                  </AdminRoute>
                                }
                              />
                              <Route
                                path="/admin/teams/:teamId"
                                element={
                                  <AdminRoute>
                                    <TeamDetail />
                                  </AdminRoute>
                                }
                              />
                              <Route
                                path="/admin/settings"
                                element={
                                  <AdminRoute>
                                    <SystemSettings />
                                  </AdminRoute>
                                }
                              />

                              <Route
                                path="*"
                                element={<Navigate to="/" replace />}
                              />
                            </Routes>
                          </AppLayout>
                        </SecurityAssistantProvider>
                      </SpaceProvider>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
